import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatDate } from "utils/formatters";
import {
  compareItemCode,
  flattenItemTree as flattenItems,
  getItemLevel,
  getLeafDescendants,
  hasChildItems as isParentItem,
} from "./itemHierarchy";

const FONT_NAME = "Times New Roman";

const COLORS = {
  title: "FF1F4E79",
  headerFill: "FFD9EAF7",
  headerText: "FF1F2937",
  border: "FFB7C9D6",
  parentFill: "FFF3F6FA",
  totalBeforeFill: "FFE3F2FD",
  totalAfterFill: "FFE8F5E9",
  white: "FFFFFFFF",
  gray: "FF9E9E9E",
  green: "FF4CAF50",
  lightGreen: "FF8BC34A",
  blue: "FF2196F3",
  orange: "FFFF9800",
  red: "FFF44336",
  purple: "FF9C27B0",
};

const COLUMNS = [
  { key: "itemCode", header: "STT", width: 12 },
  { key: "itemName", header: "Dự toán", width: 42 },
  { key: "unit", header: "ĐVT", width: 10 },
  { key: "difficultyLevel", header: "Mức KK", width: 12 },
  { key: "laborCount", header: "Định biên", width: 14 },
  { key: "laborNorm", header: "Định mức", width: 14 },
  { key: "laborPrice", header: "Đơn giá công", width: 16 },
  { key: "unitPrice", header: "Đơn giá sản phẩm", width: 18 },
  { key: "subcontractQuantity", header: "Khối lượng", width: 14 },
  { key: "subcontractAmount", header: "Thành tiền", width: 18 },
  { key: "priority", header: "Độ ưu tiên", width: 14 },
  { key: "status", header: "Trạng thái", width: 18 },
  { key: "startDate", header: "Từ ngày", width: 14 },
  { key: "endDate", header: "Đến ngày", width: 14 },
  { key: "completionPercentage", header: "Tiến độ", width: 12 },
];

const getPaymentColumns = (paymentProfiles = []) =>
  (paymentProfiles || []).flatMap((profile, index) => [
    {
      key: `payment_quantity_${profile.paymentProfileId}`,
      header: "Khối lượng",
      groupHeader: `Nghiệm thu đợt ${index + 1}${profile.paymentDate ? ` - ${formatDate(profile.paymentDate)}` : ""
        }`,
      paymentProfileId: profile.paymentProfileId,
      paymentColumnType: "quantity",
      width: 14,
    },
    {
      key: `payment_amount_${profile.paymentProfileId}`,
      header: "Thành tiền",
      groupHeader: `Nghiệm thu đợt ${index + 1}${profile.paymentDate ? ` - ${formatDate(profile.paymentDate)}` : ""
        }`,
      paymentProfileId: profile.paymentProfileId,
      paymentColumnType: "amount",
      width: 18,
    },
  ]);

const getCumulativePaymentColumns = (paymentProfiles = []) => {
  if (!paymentProfiles || paymentProfiles.length === 0) return [];

  return [
    {
      key: "cumulative_quantity",
      header: "Khối lượng",
      groupHeader: "Lũy kế nghiệm thu",
      paymentColumnType: "cumulativeQuantity",
      width: 14,
    },
    {
      key: "cumulative_amount",
      header: "Thành tiền",
      groupHeader: "Lũy kế nghiệm thu",
      paymentColumnType: "cumulativeAmount",
      width: 18,
    },
  ];
};

const border = {
  top: { style: "thin", color: { argb: COLORS.border } },
  left: { style: "thin", color: { argb: COLORS.border } },
  bottom: { style: "thin", color: { argb: COLORS.border } },
  right: { style: "thin", color: { argb: COLORS.border } },
};

const formatNumber = (value, maximumFractionDigits = 4) => {
  if (value === null || value === undefined || value === "") return "";
  let normalizedValue = value;
  if (typeof value === "string") {
    const rawValue = value.trim();
    if (rawValue.includes(".") && rawValue.includes(",")) {
      normalizedValue = rawValue.replace(/\./g, "").replace(",", ".");
    } else if (rawValue.includes(",")) {
      normalizedValue = rawValue.replace(",", ".");
    } else if (/^\d{1,3}(\.\d{3})+$/.test(rawValue)) {
      normalizedValue = rawValue.replace(/\./g, "");
    } else {
      normalizedValue = rawValue;
    }
  }
  const numberValue = Number(normalizedValue);
  if (!Number.isFinite(numberValue)) return "";
  return new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numberValue);
};

const formatMoney = (value) => formatNumber(value, 0);

const formatQuantity = (value) => formatNumber(value, 2);

const safeFileName = (value) =>
  (value || "hang-muc-hop-dong")
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 120);

const getDifficultyColor = (level) => {
  switch (Number(level)) {
    case 1:
      return COLORS.green;
    case 2:
      return COLORS.lightGreen;
    case 3:
      return COLORS.orange;
    case 4:
      return COLORS.red;
    case 5:
      return COLORS.purple;
    default:
      return COLORS.gray;
  }
};

const getPriorityColor = (priority) => {
  const normalized = priority ? priority.toString().toLowerCase().trim() : "";
  const colors = {
    "cao": COLORS.red,
    "trung bình": COLORS.orange,
    "thấp": COLORS.green,
  };
  return colors[normalized] || COLORS.gray;
};

const getStatusColor = (status) => {
  const normalized = status ? status.toString().toLowerCase().trim() : "";
  const colors = {
    "chưa bắt đầu": COLORS.gray,
    "đang thực hiện": COLORS.green,
    "hoàn thành": COLORS.blue,
    "tạm dừng": COLORS.orange,
    "hủy bỏ": COLORS.red,
  };
  return colors[normalized] || COLORS.gray;
};

const getPriorityLabel = (priority) => {
  switch (Number(priority)) {
    case 1:
      return "Cao";
    case 2:
      return "Trung bình";
    case 3:
      return "Thấp";
    default:
      return "";
  }
};

const getPaymentAmount = (sourceItem, payment) =>
  payment.paymentAmount && payment.paymentAmount > 0
    ? Math.round(payment.paymentAmount)
    : Math.round((sourceItem?.unitPrice || 0) * (payment.paymentQuantity || 0));

const getPaymentValue = (item, profileId, type, subcontractPayments = []) => {
  if (!profileId) return "";

  if (isParentItem(item)) {
    if (type === "quantity") return "";

    const leafItems = getLeafDescendants(item);
    const leafById = new Map(
      leafItems.map((leafItem) => [leafItem.itemId, leafItem]),
    );
    const totalAmount = subcontractPayments
      .filter(
        (payment) =>
          payment.paymentProfileId === profileId && leafById.has(payment.subcontractItemId),
      )
      .reduce(
        (total, payment) =>
          total + getPaymentAmount(leafById.get(payment.subcontractItemId), payment),
        0,
      );

    return totalAmount ? formatMoney(totalAmount) : "";
  }

  const payment = subcontractPayments.find(
    (itemPayment) =>
      itemPayment.subcontractItemId === item.itemId &&
      itemPayment.paymentProfileId === profileId,
  );

  if (!payment) return "";

  if (type === "quantity") {
    return formatQuantity(payment.paymentQuantity);
  }

  const paymentAmount = getPaymentAmount(item, payment);
  return paymentAmount ? formatMoney(paymentAmount) : "";
};

const getCumulativePaymentValue = (item, type, subcontractPayments = []) => {
  if (isParentItem(item)) {
    if (type === "cumulativeQuantity") return "";

    const leafItems = getLeafDescendants(item);
    const leafById = new Map(
      leafItems.map((leafItem) => [leafItem.itemId, leafItem]),
    );
    const totalAmount = subcontractPayments
      .filter((payment) => leafById.has(payment.subcontractItemId))
      .reduce(
        (total, payment) =>
          total + getPaymentAmount(leafById.get(payment.subcontractItemId), payment),
        0,
      );

    return totalAmount ? formatMoney(totalAmount) : "";
  }

  const itemPayments = subcontractPayments.filter(
    (payment) => payment.subcontractItemId === item.itemId,
  );

  if (type === "cumulativeQuantity") {
    const totalQuantity = itemPayments.reduce(
      (total, payment) => total + (Number(payment.paymentQuantity) || 0),
      0,
    );

    return totalQuantity ? formatQuantity(totalQuantity) : "";
  }

  const totalAmount = itemPayments.reduce(
    (total, payment) => total + getPaymentAmount(item, payment),
    0,
  );

  return totalAmount ? formatMoney(totalAmount) : "";
};

const getPaymentProfileTotal = (profileId, subcontractPayments = [], items = []) => {
  const leafItems = flattenItems(items).filter((item) => !isParentItem(item));
  const itemById = new Map(leafItems.map((item) => [item.itemId, item]));

  return subcontractPayments
    .filter((payment) => payment.paymentProfileId === profileId)
    .reduce(
      (total, payment) =>
        total + getPaymentAmount(itemById.get(payment.subcontractItemId), payment),
      0,
    );
};

const getCumulativePaymentTotal = (subcontractPayments = [], items = []) => {
  const leafItems = flattenItems(items).filter((item) => !isParentItem(item));
  const itemById = new Map(leafItems.map((item) => [item.itemId, item]));

  return subcontractPayments.reduce(
    (total, payment) =>
      total + getPaymentAmount(itemById.get(payment.subcontractItemId), payment),
    0,
  );
};

const applyCellBaseStyle = (cell) => {
  cell.border = border;
  cell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true,
  };
};

const addInfoRow = (worksheet, label, value, totalColumns = COLUMNS.length) => {
  const row = worksheet.addRow([label?.toUpperCase(), "", value ?? ""]);
  worksheet.mergeCells(row.number, 1, row.number, 2);
  worksheet.mergeCells(row.number, 3, row.number, totalColumns);
  row.getCell(1).font = { bold: true, name: `${FONT_NAME}` };
  row.getCell(1).alignment = { horizontal: "right" };
  row.getCell(3).font = { name: `${FONT_NAME}` };
  row.getCell(3).alignment = { horizontal: "left", wrapText: true };
  return row;
};

const styleDataRow = (row, rowData, totalColumns = COLUMNS.length) => {
  row.eachCell({ includeEmpty: true }, applyCellBaseStyle);

  if (rowData.level === 0) {
    row.font = { bold: true, name: `${FONT_NAME}` };
    row.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.parentFill },
      };
    });
  } else {
    row.font = {
      bold: false,
      name: FONT_NAME,
    };
  }

  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(2).alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
    indent: Math.min(rowData.level || 0, 8),
  };
  [3, 4, 8, 9, 12].forEach((index) => {
    row.getCell(index).alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
  });
  [5, 6, 7].forEach((index) => {
    row.getCell(index).alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };
  });
  [10, 11].forEach((index) => {
    row.getCell(index).alignment = { horizontal: "center", vertical: "middle" };
  });
  for (let index = COLUMNS.length + 1; index <= totalColumns; index += 1) {
    row.getCell(index).alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };
  }

  if (rowData.difficultyLevel) {
    row.getCell(4).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getDifficultyColor(rowData.difficultyLevel) },
    };
    row.getCell(4).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }

  if (rowData.priority) {
    row.getCell(11).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getPriorityColor(rowData.priority) },
    };
    row.getCell(11).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }

  if (rowData.status) {
    row.getCell(12).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getStatusColor(rowData.status) },
    };
    row.getCell(12).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }
};

const addSummaryRow = (
  worksheet,
  label,
  amount,
  fillColor,
  exportColumns = COLUMNS,
  paymentProfileTotals = {},
  cumulativeTotals = {},
) => {
  const rowValues = exportColumns.map((column) => {
    if (column.key === "itemCode") return "TỔNG";
    if (column.key === "itemName") return label;
    if (column.key === "subcontractAmount") {
      return amount !== null && amount !== undefined && amount !== ""
        ? formatMoney(amount)
        : "";
    }
    if (column.paymentColumnType === "amount") {
      const total = paymentProfileTotals[column.paymentProfileId];
      return total !== null && total !== undefined && total !== ""
        ? formatMoney(total)
        : "";
    }
    if (column.paymentColumnType === "cumulativeAmount") {
      const total = cumulativeTotals?.cumulativeAmount;
      return total !== null && total !== undefined && total !== ""
        ? formatMoney(total)
        : "";
    }
    return "";
  });
  const row = worksheet.addRow(rowValues);
  row.eachCell({ includeEmpty: true }, applyCellBaseStyle);
  row.font = { bold: true, name: `${FONT_NAME}` };
  row.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fillColor },
    };
  });
  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
  row.getCell(7).alignment = { horizontal: "right", vertical: "middle" };
  row.getCell(7).numFmt = "#,##0";
  for (let index = COLUMNS.length + 1; index <= exportColumns.length; index += 1) {
    row.getCell(index).alignment = { horizontal: "right", vertical: "middle" };
    row.getCell(index).numFmt = "#,##0";
  }
};

export const exportContractItemsExcel = async ({
  items = [],
  subcontract = {},
  paymentProfiles = [],
  subcontractPayments = [],
  summaryTotals = {},
  exportedBy = "",
} = {}) => {
  const paymentColumns = getPaymentColumns(paymentProfiles);
  const cumulativePaymentColumns = getCumulativePaymentColumns(paymentProfiles);
  const exportColumns = [...COLUMNS, ...paymentColumns, ...cumulativePaymentColumns];
  const totalColumns = exportColumns.length;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = exportedBy || "Project Trak";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Dự toán giao khoán", {
    views: [{ state: "frozen", ySplit: 10 }],
  });

  worksheet.columns = exportColumns.map((column) => ({
    key: column.key,
    width: column.width,
  }));

  worksheet.mergeCells(1, 1, 1, totalColumns);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = "DỰ TOÁN GIAO KHOÁN";
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.title }, name: `${FONT_NAME}` };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 28;

  const subcontractName =
    subcontract?.subcontractName || subcontract?.name || subcontract?.title || "";
  const subcontractCode =
    subcontract?.subcontractNumber || subcontract?.subcontractCode || subcontract?.code || "";
  const customerName =
    subcontract?.clientName ||
    subcontract?.customerName ||
    subcontract?.investorName ||
    subcontract?.ownerName ||
    "";

  addInfoRow(worksheet, "Giao khoán: ", subcontractName, totalColumns);
  addInfoRow(worksheet, "Mã giao khoán: ", subcontractCode, totalColumns);
  addInfoRow(worksheet, "Chủ đầu tư / khách hàng: ", customerName, totalColumns);
  addInfoRow(worksheet, "Ngày xuất file: ", formatDate(new Date()), totalColumns);
  addInfoRow(worksheet, "Người xuất: ", exportedBy, totalColumns);
  addInfoRow(
    worksheet,
    "Tổng giá trị trước thuế: ",
    formatMoney(
      summaryTotals.totalBeforeTaxRounded ?? summaryTotals.totalBeforeTax,
    ),
    totalColumns,
  );
  addInfoRow(
    worksheet,
    "Tổng giá trị sau thuế",
    formatMoney(
      summaryTotals.totalAfterTaxRoundedForSummary ??
      summaryTotals.totalAfterTaxRounded ??
      summaryTotals.totalAfterTax,
    ),
    totalColumns,
  );
  worksheet.addRow([]);

  const hasPaymentColumns = paymentColumns.length > 0 || cumulativePaymentColumns.length > 0;
  const headerRow = worksheet.addRow(
    exportColumns.map((column) => column.groupHeader || column.header),
  );
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    applyCellBaseStyle(cell);
    cell.font = { bold: true, color: { argb: COLORS.headerText }, name: `${FONT_NAME}` };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.headerFill },
    };
  });

  let subHeaderRow = null;
  if (hasPaymentColumns) {
    subHeaderRow = worksheet.addRow(
      exportColumns.map((column) => (column.groupHeader ? column.header : "")),
    );
    subHeaderRow.height = 24;
    subHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
      applyCellBaseStyle(cell);
      cell.font = { bold: true, color: { argb: COLORS.headerText }, name: `${FONT_NAME}` };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.headerFill },
      };
    });

    for (let index = 1; index <= COLUMNS.length; index += 1) {
      worksheet.mergeCells(headerRow.number, index, subHeaderRow.number, index);
    }

    paymentProfiles.forEach((profile) => {
      const firstColumnIndex =
        COLUMNS.length +
        paymentProfiles.findIndex(
          (item) => item.paymentProfileId === profile.paymentProfileId,
        ) *
        2 +
        1;
      worksheet.mergeCells(
        headerRow.number,
        firstColumnIndex,
        headerRow.number,
        firstColumnIndex + 1,
      );
    });

    if (cumulativePaymentColumns.length > 0) {
      const firstCumulativeColumnIndex =
        COLUMNS.length + paymentColumns.length + 1;
      worksheet.mergeCells(
        headerRow.number,
        firstCumulativeColumnIndex,
        headerRow.number,
        firstCumulativeColumnIndex + cumulativePaymentColumns.length - 1,
      );
    }
  }

  worksheet.views = [
    { state: "frozen", ySplit: hasPaymentColumns ? subHeaderRow.number : headerRow.number },
  ];

  flattenItems(items).forEach((item) => {
    const rowData = {
      itemCode: item.itemCode || "",
      itemName: item.itemName || "",
      unit: item.unit || "",
      difficultyLevel: isParentItem(item) ? "" : item.difficultyLevel || "",
      subcontractQuantity: isParentItem(item)
        ? ""
        : formatQuantity(item.subcontractQuantity),
      unitPrice: isParentItem(item) ? "" : formatMoney(item.unitPrice),
      laborCount: isParentItem(item) ? "" : formatQuantity(item.laborCount),
      laborNorm: isParentItem(item) ? "" : formatQuantity(item.laborNorm),
      laborPrice: isParentItem(item) ? "" : formatMoney(item.laborPrice),
      subcontractAmount: formatMoney(item.subcontractAmount),
      priority: isParentItem(item) ? "" : getPriorityLabel(item.priority),
      status: isParentItem(item) ? "" : item.status || "",
      startDate: isParentItem(item) ? "" : formatDate(item.startDate),
      endDate: isParentItem(item) ? "" : formatDate(item.endDate),
      completionPercentage:
        isParentItem(item) ||
          item.completionPercentage === null ||
          item.completionPercentage === undefined
          ? ""
          : `${Math.round(Number(item.completionPercentage) || 0)}%`,
      level: item.level_,
      isParent: isParentItem(item),
    };
    paymentColumns.forEach((column) => {
      rowData[column.key] = getPaymentValue(
        item,
        column.paymentProfileId,
        column.paymentColumnType,
        subcontractPayments,
      );
    });
    cumulativePaymentColumns.forEach((column) => {
      rowData[column.key] = getCumulativePaymentValue(
        item,
        column.paymentColumnType,
        subcontractPayments,
      );
    });

    const row = worksheet.addRow(exportColumns.map((column) => rowData[column.key]));
    styleDataRow(row, rowData, totalColumns);
    row.getCell(6).numFmt = "#,##0";
    row.getCell(7).numFmt = "#,##0";
    paymentColumns.forEach((column, index) => {
      const cell = row.getCell(COLUMNS.length + index + 1);
      if (column.paymentColumnType === "amount") {
        cell.numFmt = "#,##0";
      }
    });
    cumulativePaymentColumns.forEach((column, index) => {
      const cell = row.getCell(
        COLUMNS.length + paymentColumns.length + index + 1,
      );
      if (column.paymentColumnType === "cumulativeAmount") {
        cell.numFmt = "#,##0";
      }
    });
  });

  if (items.length > 0) {
    const paymentProfileTotals = Object.fromEntries(
      paymentProfiles.map((profile) => [
        profile.paymentProfileId,
        getPaymentProfileTotal(profile.paymentProfileId, subcontractPayments, items),
      ]),
    );
    const afterTaxPaymentProfileTotals = Object.fromEntries(
      paymentProfiles.map((profile) => {
        const beforeTaxAmount = paymentProfileTotals[profile.paymentProfileId] || 0;
        const vatRate = (profile.vat || subcontract?.vat || 8) / 100;
        return [
          profile.paymentProfileId,
          Math.round(beforeTaxAmount * (1 + vatRate)),
        ];
      }),
    );
    const cumulativePaymentTotal = getCumulativePaymentTotal(
      subcontractPayments,
      items,
    );
    const afterTaxCumulativePaymentTotal = paymentProfiles.reduce(
      (total, profile) => {
        const beforeTaxAmount = paymentProfileTotals[profile.paymentProfileId] || 0;
        const vatRate = (profile.vat || subcontract?.vat || 8) / 100;
        return total + Math.round(beforeTaxAmount * (1 + vatRate));
      },
      0,
    );
    addSummaryRow(
      worksheet,
      "(trước thuế)",
      summaryTotals.totalBeforeTaxRounded ?? summaryTotals.totalBeforeTax,
      COLORS.totalBeforeFill,
      exportColumns,
      paymentProfileTotals,
      { cumulativeAmount: cumulativePaymentTotal },
    );
    addSummaryRow(
      worksheet,
      "(sau thuế)",
      summaryTotals.totalAfterTaxRoundedForSummary ??
      summaryTotals.totalAfterTaxRounded ??
      summaryTotals.totalAfterTax,
      COLORS.totalAfterFill,
      exportColumns,
      afterTaxPaymentProfileTotals,
      { cumulativeAmount: afterTaxCumulativePaymentTotal },
    );
  }

  worksheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: hasPaymentColumns ? subHeaderRow.number : headerRow.number, column: totalColumns },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const suffix = new Date().toISOString().slice(0, 10);
  saveAs(blob, `${safeFileName(subcontractCode || subcontractName)}-${suffix}.xlsx`);
};

const SECTION_SUFFIX = {
  ESTIMATE: "DuToan",
  COMMUNE_DETAIL: "ChiTietMoiXa",
  PROVINCE_COMMUNE: "PhanCapTinhXa",
};

const normalize = (value) => String(value || "").trim().toUpperCase();
const sheetName = (value, fallback = "Scope") =>
  (value || fallback).toString().replace(/[\\/?*[\]:]/g, "-").slice(0, 31) || fallback;
const addScopeWorksheet = ({
  workbook,
  section,
  scope,
  subcontract,
  paymentProfiles,
  subcontractPayments,
  summaryTotals,
  exportedBy,
}) => {
  const paymentColumns = getPaymentColumns(paymentProfiles);
  const cumulativePaymentColumns = getCumulativePaymentColumns(paymentProfiles);
  const exportColumns = [...COLUMNS, ...paymentColumns, ...cumulativePaymentColumns];
  const totalColumns = exportColumns.length;
  const worksheet = workbook.addWorksheet(sheetName(scope.scopeName), {
    views: [{ state: "frozen", ySplit: 9 }],
  });
  worksheet.columns = exportColumns.map((column) => ({ key: column.key, width: column.width }));

  worksheet.mergeCells(1, 1, 1, totalColumns);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = "HẠNG MỤC QUYẾT ĐỊNH GIAO KHOÁN";
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.title }, name: FONT_NAME };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 28;

  const subcontractName = subcontract?.subcontractName || subcontract?.name || subcontract?.title || "";
  const subcontractCode = subcontract?.subcontractNumber || subcontract?.subcontractCode || subcontract?.code || "";
  addInfoRow(worksheet, "Giao khoán: ", subcontractName, totalColumns);
  addInfoRow(worksheet, "Mã giao khoán: ", subcontractCode, totalColumns);
  addInfoRow(worksheet, "Loại hạng mục: ", section?.sectionName || "", totalColumns);
  addInfoRow(worksheet, "Phạm vi: ", scope?.scopeName || "", totalColumns);
  addInfoRow(worksheet, "Ngày xuất file: ", formatDate(new Date()), totalColumns);
  if (exportedBy) addInfoRow(worksheet, "Người xuất: ", exportedBy, totalColumns);
  worksheet.addRow([]);

  const headerRow = worksheet.addRow(exportColumns.map((column) => column.groupHeader || column.header));
  headerRow.eachCell((cell) => {
    applyCellBaseStyle(cell);
    cell.font = { bold: true, color: { argb: COLORS.headerText }, name: FONT_NAME };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerFill } };
  });

  const hasPaymentColumns = paymentColumns.length > 0 || cumulativePaymentColumns.length > 0;
  let subHeaderRow = null;
  if (hasPaymentColumns) {
    subHeaderRow = worksheet.addRow(exportColumns.map((column) => (column.groupHeader ? column.header : "")));
    subHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
      applyCellBaseStyle(cell);
      cell.font = { bold: true, color: { argb: COLORS.headerText }, name: FONT_NAME };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerFill } };
    });
    for (let index = 1; index <= COLUMNS.length; index += 1) {
      worksheet.mergeCells(headerRow.number, index, subHeaderRow.number, index);
    }
  }

  const items = [...(scope?.items || [])].sort(compareItemCode);
  items.forEach((item) => {
    const rowData = {
      itemCode: item.itemCode || "",
      itemName: item.itemName || "",
      unit: item.unit || "",
      difficultyLevel: isParentItem(item) ? "" : item.difficultyLevel || "",
      subcontractQuantity: isParentItem(item) ? "" : formatQuantity(item.subcontractQuantity),
      unitPrice: isParentItem(item) ? "" : formatMoney(item.unitPrice),
      laborCount: isParentItem(item) ? "" : formatQuantity(item.laborCount),
      laborNorm: isParentItem(item) ? "" : formatQuantity(item.laborNorm),
      laborPrice: isParentItem(item) ? "" : formatMoney(item.laborPrice),
      subcontractAmount: formatMoney(item.subcontractAmount),
      priority: isParentItem(item) ? "" : getPriorityLabel(item.priority),
      status: isParentItem(item) ? "" : item.status || "",
      startDate: isParentItem(item) ? "" : formatDate(item.startDate),
      endDate: isParentItem(item) ? "" : formatDate(item.endDate),
      completionPercentage:
        isParentItem(item) || item.completionPercentage === null || item.completionPercentage === undefined
          ? ""
          : `${Math.round(Number(item.completionPercentage) || 0)}%`,
      level: getItemLevel(item),
      isParent: isParentItem(item),
    };
    paymentColumns.forEach((column) => {
      rowData[column.key] = getPaymentValue(item, column.paymentProfileId, column.paymentColumnType, subcontractPayments);
    });
    cumulativePaymentColumns.forEach((column) => {
      rowData[column.key] = getCumulativePaymentValue(item, column.paymentColumnType, subcontractPayments);
    });

    const row = worksheet.addRow(exportColumns.map((column) => rowData[column.key]));
    styleDataRow(row, rowData, totalColumns);
  });

  if (items.length > 0) {
    const amount = items
      .filter((item) => !isParentItem(item))
      .reduce((total, item) => total + Number(item.subcontractAmount || 0), 0);
    const paymentProfileTotals = Object.fromEntries(
      paymentProfiles.map((profile) => [
        profile.paymentProfileId,
        getPaymentProfileTotal(profile.paymentProfileId, subcontractPayments, items),
      ]),
    );
    addSummaryRow(
      worksheet,
      "(trước thuế)",
      summaryTotals.totalBeforeTaxRounded ?? summaryTotals.totalBeforeTax ?? amount,
      COLORS.totalBeforeFill,
      exportColumns,
      paymentProfileTotals,
      { cumulativeAmount: getCumulativePaymentTotal(subcontractPayments, items) },
    );
  }

  worksheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: hasPaymentColumns ? subHeaderRow.number : headerRow.number, column: totalColumns },
  };
};

export const exportSubcontractItemsExcel = async ({
  sections = [],
  subcontract = {},
  paymentProfiles = [],
  subcontractPayments = [],
  summaryTotals = {},
  exportedBy = "",
} = {}) => {
  const subcontractName = subcontract?.subcontractName || subcontract?.name || "";
  const subcontractCode = subcontract?.subcontractNumber || subcontract?.subcontractCode || subcontract?.code || subcontractName;
  const suffix = new Date().toISOString().slice(0, 10);

  for (const section of sections) {
    const scopes = section.scopes || [];
    if (scopes.length === 0) continue;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = exportedBy || "Project Trak";
    workbook.created = new Date();
    scopes.forEach((scope) => addScopeWorksheet({
      workbook,
      section,
      scope,
      subcontract,
      paymentProfiles,
      subcontractPayments: subcontractPayments.filter((payment) => {
        const scopeItems = new Set((scope.items || []).map((item) => item.itemId));
        return scopeItems.has(payment.subcontractItemId);
      }),
      summaryTotals,
      exportedBy,
    }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const sectionSuffix = SECTION_SUFFIX[normalize(section.sectionType)] || safeFileName(section.sectionName || "Section");
    saveAs(blob, `${safeFileName(subcontractCode)}_${sectionSuffix}_${suffix}.xlsx`);
  }
};

export default exportSubcontractItemsExcel;
